/**
 * @module  Experience/Shared-Browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// tslint:disable:max-file-line-count
/* tslint:disable:no-any */ // Need to support all data types
// Core Libraries
import { Injectable } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';

// Services
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { UserService } from '@support/shared/services/user.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { CreditCheckService } from '@experience/shop/credit-check/services/credit-check.service';
// Interfaces
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { SelectItem } from '@support/shared/interfaces/select-item.interface';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import {
  BrowseEntryAEMInterface,
  BrowseEntryConfigData,
} from '@core/aem/interfaces/common/browse-entry.interface';
import {
  CompareDeviceRequestPayload,
  HardgoodOffer,
} from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import { ProductInfo } from '@experience/shop/hardgoods/interfaces/compare-hardgoods.interface';
import { CustomerDetailsResponse } from '@support/api/proxies/responses/api-2007.interface';
import { EstimatedCreditCheck } from '@support/api/proxies/responses/api-2033.interface';

// Constants
import {
  SHARED_BROWSE_CONSTANTS,
  CreditLevelEnum,
  BillingTypeEnum,
  SHARED_BROWSE_COMPONENTS,
  ZIP_CODE_CONSTANTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { CREDIT_CLASS } from '@experience/shop/credit-check/constants/credit-check.constants';
import { APIError } from '@support/api/common/error.interface';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import {
  of as observableOf,
  throwError as observableThrowError,
  Observable,
  Subject,
  BehaviorSubject,
} from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { AppConfig } from '@root/app.config';
import { FccLabelContent } from '@experience/shop/softgoods/interfaces/family-plan.interface';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
/**
 * Service for Shop/Sharded-Browse Module
 */
@Injectable()
export class SharedBrowseService {
  public productDetails: HardgoodOffer[];

  public selectedProductOfferIds: CompareDeviceRequestPayload[];
  public compareState: String;

  public browsePricingUpdated$: Observable<boolean>;

  /**
   * Selected Route
   */
  public selectedRoute: string;

  private browsePricingUpdatedSource: Subject<boolean>;

  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  public planFamiliesContent: Object;
  public promotionTagIconTemplate: string;

  public zipModalNotAvailableSource: BehaviorSubject<boolean>;

  /**
   * Hold Upper Funnel Browse Zip Code Address details
   */
  private browseValidatedAddress: Address;

  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  public zipCodeNotAvailableSource: BehaviorSubject<boolean>;

  private enableStoredZipCode: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private formCreationService: FormCreationService,
    private creditCheckService: CreditCheckService,
    private commonApiService: CommonApiService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private contentProviderService: ContentProviderService,
    private logger: LoggerService,
    private userService: UserService,
    private objectService: ObjectService,
    private apiGateway: ApiGatewayService,
    private appConfig: AppConfig
  ) {
    this.browsePricingUpdatedSource = new Subject();
    this.browsePricingUpdated$ = this.browsePricingUpdatedSource.asObservable();
    this.ngUnsubscribe = new Subject<void>();
    this.productDetails = [];
    this.selectedProductOfferIds = [];
    this.compareState = '';
    this.zipModalNotAvailableSource = new BehaviorSubject(false);
  }
  /**
   * Build the changeZip form with validators
   */
  public buildChangeZipForm(): FormGroup {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering buildChangeZipForm() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const fieldValidationConstants = this.getFieldValidationConstants();
    const billingZip = this.objectService.get(
      this.stateStoreUtilsService.getBillingAddress(),
      'postalCode'
    );
    const zip = billingZip
      ? billingZip
      : this.objectService.get(this.getUserInputsConfigData(), 'zip');
    return this.formBuilder.group({
      zip: this.formCreationService.getFormControl(
        fieldValidationConstants.ZIP_CODE,
        zip ? zip.split('-')[0] : '',
        true
      ),
    });
  }

  /**
   * Provides update when credit estimation changed in browse page
   * @return void
   */
  public announceBrowsePriceUpdate(): void {
    this.browsePricingUpdatedSource.next(true);
  }

  /**
   * Build the changeCreditLevel form with validators
   */
  public buildChangeCreditLevelForm(): FormGroup {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering buildChangeCreditLevelForm() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const storedEntry = this.getUserInputsConfigData();
    return this.formBuilder.group({
      creditLevel: this.formCreationService.getFormControl(
        null,
        storedEntry && storedEntry.creditLevel ? storedEntry.creditLevel.toString() : '',
        true
      ),
    });
  }

  /**
   * Build the chagneBillingLevel form with validators
   */
  public buildChangeBillingLevelForm(): FormGroup {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering buildChangeBillingLevelForm() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const storedEntry = this.getUserInputsConfigData();
    return this.formBuilder.group({
      billingType: this.formCreationService.getFormControl(
        null,
        storedEntry ? this.objectService.get(storedEntry, 'billingType', ' ').toString() : '',
        true
      ),
    });
  }

  /**
   * Returns a boolean based on if a credit check has occured
   */
  public checkCustomerCredit(): boolean {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering checkCustomerCredit() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const customerConfigData = this.getUserInputsConfigData();
    return !!(
      customerConfigData &&
      customerConfigData.creditCheckResponse &&
      customerConfigData.creditCheckResponse.creditLevel
    );
  }

  /**
   * Returns constants for the sharedBrowse Module
   */
  public getSharedBrowseConstants(): ObjectInterface {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getSharedBrowseConstants() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return SHARED_BROWSE_CONSTANTS;
  }

  /**
   * Returns API 2004 response
   */
  public validateAddress(form: FormGroup, zip?: string): Observable<Address[]> {
    return this.creditCheckService.validateAddress(form, null, zip);
  }

  /**
   * Returns all credit levels from the constants file
   */
  public getCreditLevelsDropdownValues(aemContent: BrowseEntryConfigData): SelectItem[] {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getCreditLevelsDropdownValues() in ' +
        SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const creditLevels = [];
    const creditLevelMapping = this.getCreditLevelMappingValues(aemContent);
    for (const creditLevelType in creditLevelMapping) {
      if (creditLevelMapping.hasOwnProperty(creditLevelType)) {
        creditLevels.push({ label: creditLevelMapping[creditLevelType], value: creditLevelType });
      }
    }
    return creditLevels;
  }

  /**
   *  Returns all credit levels from the constants file
   */
  public getBillingLevelsDropdownValues(aemContent: BrowseEntryConfigData): SelectItem[] {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getBillingLevelsDropdownValues() in ' +
        SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const billingLevels = [];
    const billingTypeMapping = this.getBillingTypeMappingValues(aemContent);
    for (const billingLevelType in billingTypeMapping) {
      if (billingTypeMapping.hasOwnProperty(billingLevelType)) {
        billingLevels.push({
          label: billingTypeMapping[billingLevelType],
          value: billingLevelType,
        });
      }
    }
    return billingLevels;
  }

  /**
   * Returns constant values for credit level mappings
   */
  public getCreditLevelMappingValues(aemContent: BrowseEntryConfigData): ObjectInterface {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getCreditLevelMappingValues() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return {
      [CreditLevelEnum.EXCELLENT_CREDIT]: aemContent.excellentCredit,
      [CreditLevelEnum.AVERAGE_CREDIT]: aemContent.averageCredit,
      [CreditLevelEnum.BUILDING_CREDIT]: aemContent.buildingCredit,
    };
  }

  /**
   * Returns mapping of selected values to API mapping for credit level
   */
  public getCreditLevelAPIMapping(creditEnum: CreditLevelEnum): string {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getCreditLevelAPIMapping() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    let apiMappingValue = '';
    if (creditEnum === CreditLevelEnum.EXCELLENT_CREDIT) {
      apiMappingValue = SHARED_BROWSE_CONSTANTS.excellentCreditAPIMap;
    } else if (creditEnum === CreditLevelEnum.AVERAGE_CREDIT) {
      apiMappingValue = SHARED_BROWSE_CONSTANTS.avarageCreditAPIMap;
    } else if (creditEnum === CreditLevelEnum.BUILDING_CREDIT) {
      apiMappingValue = SHARED_BROWSE_CONSTANTS.buildingCreditAPIMap;
    }
    return apiMappingValue;
  }

  /**
   * Returns constant values for billing type mappings
   */
  public getBillingTypeMappingValues(aemContent: BrowseEntryConfigData): ObjectInterface {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getBillingTypeMappingValues() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return {
      [BillingTypeEnum.PREPAID]: aemContent.prepaid,
      [BillingTypeEnum.POSTPAID]: aemContent.postpaid,
    };
  }

  /**
   * Returns mapping of selected values to API mapping for billing type
   */
  public getBillingTypeAPIMapping(billingTypeEnum: BillingTypeEnum): string {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getBillingTypeAPIMapping() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    let apiMappingValue = '';
    if (billingTypeEnum === BillingTypeEnum.PREPAID) {
      apiMappingValue = SHARED_BROWSE_CONSTANTS.prepaidAPIMap;
    } else if (billingTypeEnum === BillingTypeEnum.POSTPAID) {
      apiMappingValue = SHARED_BROWSE_CONSTANTS.postpaidAPIMap;
    }
    return apiMappingValue;
  }

  /**
   * Retrieves the fieldValidationConstants from commonAPI service
   */
  public getFieldValidationConstants(): ObjectInterface {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getFieldValidationConstants() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return this.commonApiService.getFieldValidationConstants();
  }
  /**
   * Builds formgroup for zipcode validation
   * @returns Formgroup
   */
  public buildEntryModalForm(): FormGroup {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering buildEntryModalForm() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return this.formBuilder.group({
      zipCodeValidationForm: this.buildChangeZipForm(),
    });
  }
  /**
   * Gets saved user input data
   * @returns BrowseEntry from stateStoreService or default data
   */
  public getUserInputsConfigData(): BrowseEntry {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getUserInputsConfigData() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return this.stateStoreUtilsService.getCustomerConfigData();
  }

  /**
   * Save browse entry state after changes to the modal or change-entry component
   * @param entry Entry state to store in state-store service for browse entry values
   */
  public setUserInputsConfigData(data: BrowseEntry): void {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering setUserInputsConfigData() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    this.stateStoreUtilsService.setCustomerConfigData(data);
  }

  /**
   * Returns zip code for regex pattern validation
   */
  public getRegexPatternForZipCode(zipCodeIdentifier: string): string {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      ' returning zip code for regex pattern validation with getRegexPatternForZipCode() in ' +
        SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    const zipCodeValidation = this.commonApiService.getFieldValidation(zipCodeIdentifier);

    if (zipCodeValidation && zipCodeValidation.regex) {
      return zipCodeValidation.regex;
    } else {
      return '';
    }
  }

  /**
   * Retrieves AEM content for the shared-browse common component (entry modal and change entry component)
   */
  public getBrowseEntryAEMContent(): Observable<BrowseEntryConfigData> {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getBrowseEntryAEMContent() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return this.contentProviderService.getAEMRequest(CONTENT_ENDPOINT.BROWSE_ENTRY_MODAL).pipe(
      map((response: HttpResponse<Object>) => {
        this.logger.debug(
          SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
          SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
          'received AEM Content response in ' +
            SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE +
            ' in getBrowseEntryAEMContent(): ' +
            JSON.stringify(response)
        );
        return response.body;
      }),
      map((data: BrowseEntryAEMInterface) => {
        return data.model['model-data']['module-enhancements'].browseModel;
      }),
      catchError((err: APIError) => {
        this.logger.error(
          SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
          SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
          'getBrowseEntryAEMContent() request failed in ' +
            SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE +
            'with: ' +
            JSON.stringify(err)
        );
        return observableThrowError(err);
      })
    );
  }

  /**
   * @ngdoc method
   * @name setProductDetails
   * @param selectedKey of type string, hardgoodData of type HardgoodObject
   * @returns void
   * @description creates model to show all compared devices
   */
  public setProductDetails(
    hardgoodData: HardgoodOffer,
    selectedCheckBox: boolean,
    color?: string
  ): void {
    if (selectedCheckBox && !this.isHardgoodSelected(hardgoodData.offerId)) {
      this.productDetails.push(hardgoodData);
      this.selectedProductOfferIds.push({
        offerId: hardgoodData.offerId,
        color,
      });
    } else if (!selectedCheckBox && this.isHardgoodSelected(hardgoodData.offerId)) {
      this.productDetails = this.productDetails.filter(
        (productDetails: ProductInfo) => productDetails.offerId !== hardgoodData.offerId
      );
      this.selectedProductOfferIds = this.selectedProductOfferIds.filter(
        (selectedItem: CompareDeviceRequestPayload) => selectedItem.offerId !== hardgoodData.offerId
      );
    }
  }
  /**
   * Method is used to get Length of array
   * @returns {number}
   */
  public getProductDetailsLength(): number {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      ' entering getProductDetailsLength() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return this.productDetails.length;
  }

  /**
   * API_2033 call to get the reference details
   * @returns Observable<EstimatedCreditCheck>
   */
  public postReferenceDetails(creditLevel: CreditLevelEnum): Observable<EstimatedCreditCheck> {
    const estimatedCreditLevel = ['A', 'C', 'D'];
    const requestPayload = {
      creditClass: estimatedCreditLevel[creditLevel],
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2033_postReferenceDetails, requestPayload, null, null)
      .pipe(
        map((response: HttpResponse<EstimatedCreditCheck>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method is used to check whether selected offer exists or not
   * @param offerId
   * @returns {boolean}
   */
  public isHardgoodSelected(offerId: string): boolean {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering isHardgoodSelected() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    if (
      this.productDetails.filter(
        (productDetails: ProductInfo) => productDetails.offerId === offerId
      ).length > 0
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * to get product details
   * @returns {ProductInfo[]}
   */
  public getProductDetails(): ProductInfo[] {
    this.logger.debug(
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE,
      SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE,
      'entering getProductDetails() in ' + SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_SERVICE
    );
    return this.productDetails;
  }

  /**
   * To get customer Details
   * @returns boolean
   */
  public isCustomerDetailsAvailable(): Observable<boolean> {
    if (this.stateStoreUtilsService.getAuthCustomerId()) {
      this.userService.getCustomerDetailSearch().subscribe((response: CustomerDetailsResponse) => {
        const customerData: BrowseEntry = this.getUserInputsConfigData() || {};
        customerData.zip =
          this.objectService.get(response, 'customer.accounts.0.zipCode') ||
          this.objectService.get(response, 'customer.accounts.0.addresses.0.zip');

        if (this.objectService.get(response, 'customer.creditClass') === CREDIT_CLASS.SUCCESS) {
          customerData.creditLevel = CreditLevelEnum.EXCELLENT_CREDIT;
        }
        const billingType = this.stateStoreUtilsService.getCustomerBillingType();
        customerData.billingType =
          billingType &&
          billingType.toLowerCase() === SHARED_BROWSE_CONSTANTS.prepaidAPIMap.toLowerCase()
            ? BillingTypeEnum.PREPAID
            : BillingTypeEnum.POSTPAID;
        this.setUserInputsConfigData(customerData);
        return observableOf(true);
      });
    } else {
      return observableOf(false);
    }
    return observableOf(true);
  }

  /**
   * To get store zip code
   * @returns {string}
   */
  public getStoreZipCode(): string {
    const storeData = this.stateStoreUtilsService.getStoreData();
    let zipCode = this.objectService.get(storeData, 'address.zip');
    if (zipCode && zipCode.indexOf('-') > 0) {
      zipCode = zipCode.substr(0, zipCode.indexOf('-'));
    }

    this.stateStoreUtilsService.setStoreZipCode(zipCode);
    return zipCode;
  }

  public setFccBroadbandFacts(id: string): FccLabelContent {
    if (!this.planFamiliesContent) {
      this.setPlanFamiliesContent();
    }
    const fccLabelContent: FccLabelContent = { fccLabelText: '', fccLabelUrl: '' };
    if (this.planFamiliesContent) {
      const addressApiResponse = this.stateStoreUtilsService.getBillingAddress();
      if (this.planFamiliesContent && addressApiResponse) {
        fccLabelContent['fccLabelText'] = this.objectService.get(
          this.planFamiliesContent,
          'FCC_label'
        );
        if (id == SHARED_BROWSE_CONSTANTS.TMO_PREPAID_CORE_OFFER_FAMILY_NAME) {
          fccLabelContent['fccLabelUrl'] = this.objectService.get(
            this.planFamiliesContent,
            `${SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN}.${addressApiResponse.stateCode}.FCC_labelURL`,
            this.objectService.get(
              this.planFamiliesContent,
              `${SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN}.FCC_labelURL`
            )
          );
        } else {
          fccLabelContent['fccLabelUrl'] = this.objectService.get(
            this.planFamiliesContent,
            `${id}.${addressApiResponse.stateCode}.FCC_labelURL`,
            this.objectService.get(this.planFamiliesContent, `${id}.FCC_labelURL`)
          );
        }
      }
    }
    return fccLabelContent;
  }

  public setPlanFamiliesContent() {
    this.planFamiliesContent = JSON.parse(this.appConfig.getConfig('planFamiliesContent'));
    console.log(
      '🚀 ~ SharedBrowseService ~ setPlanFamiliesContent ~ this.planFamiliesContent:',
      this.planFamiliesContent
    );
    this.promotionTagIconTemplate = this.appConfig.getConfig('svg_tag_empty');
  }

  public updateBrowseZipCode(address: Address, subject?: Subject<boolean>): void {
    this.browseValidatedAddress = address;
    this.isAnon()
      ? (this.setZipCode(address.postalCode, false, false), this.setCustomerData(address))
      : this.setAuthBillingAddress(address, subject);
  }

  private setAuthBillingAddress(address: Address, subject?: Subject<boolean>): void {
    if (!address.addressid) {
      this.validateZip(address.postalCode, subject, true);
    } else {
      this.stateStoreUtilsService.setAuthBillingAddress(address);
      this.stateStoreUtilsService.setBillingAddress(address);
      if (subject) {
        subject.next(true);
      }
    }
  }

  public getDefaultZipCode(): Address {
    const defaultZipCode = this.appConfig.getConfig('defaultZipCode');
    return defaultZipCode ? { postalCode: defaultZipCode } : {};
  }

  public setZipCode(
    zipCode: string = '',
    setDefault: boolean = false,
    validationRequired: boolean = false,
    subject?: Subject<boolean>
  ): void {
    if (this.isAnon()) {
      const unverifiedZipCode =
        setDefault || !zipCode ? this.getDefaultZipCode().postalCode || '98006' : zipCode;
      if (
        !(this.browseValidatedAddress && this.browseValidatedAddress.postalCode) ||
        validationRequired
      ) {
        this.validateZip(unverifiedZipCode, subject);
      } else if (this.browseValidatedAddress && this.browseValidatedAddress.postalCode) {
        this.setStoredZipCode();
        this.stateStoreUtilsService.setBillingAddress(this.browseValidatedAddress);
        this.stateStoreUtilsService.setBrowseAddress(this.browseValidatedAddress);
        this.setCustomerData(this.browseValidatedAddress);
        this.zipModalNotAvailableSource.next(true);
        if (subject) {
          subject.next(true);
        }
      }
    }
  }

  private setCustomerData(address: Address): void {
    const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData() || {};
    customerConfigData.zip = address.postalCode;
    customerConfigData.addresses = {
      addressid: address.addressid,
      postalCode: address.postalCode,
      cityName: address.cityName,
      stateCode: address.stateCode,
    };
    this.setUserInputsConfigData(customerConfigData);
  }

  /**
   * Function used to validate address
   * @param {string} zip
   * @param {Subject<boolean>} subject
   * @param {HTMLElement} getDomReference
   */
  private validateZip(
    zipCode: string,
    subject: Subject<boolean>,
    isAuthBillingAddress?: boolean
  ): void {
    this.validateAddress(null, zipCode)
      .pipe(finalize(() => {}))
      .subscribe((address: Address[]) => {
        if (address && address[0]) {
          this.browseValidatedAddress = {
            postalCode: zipCode,
            addressid: address[0].addressid,
            cityName: address[0].cityName,
            stateCode: address[0].stateCode,
          };
          if (isAuthBillingAddress) {
            this.setAuthBillingAddress(this.browseValidatedAddress, subject);
          } else {
            this.setZipCode(zipCode, false, false, subject);
          }
        }
      });
  }

  public enableSetStoredZipCode(): void {
    this.enableStoredZipCode = true;
  }

  /**
   * Method to set zip code and expiration date
   * @param zipCode
   * @param expirationHours
   */
  public setStoredZipCode(): void {
    if (!this.stateStoreUtilsService.getAuthCustomerId() && this.enableStoredZipCode) {
      this.enableStoredZipCode = false;
      const expiryHoursConfig = this.appConfig.getConfig('zipCodeCacheExpiryHrs');
      const expiryHours =
        !expiryHoursConfig || isNaN(expiryHoursConfig)
          ? ZIP_CODE_CONSTANTS.CACHE_HOURS
          : Number(expiryHoursConfig);
      localStorage.setItem(ZIP_CODE_CONSTANTS.ZIP_CODE_KEY, this.browseValidatedAddress.postalCode);
      const expirationTime = Date.now() + expiryHours * 60 * 60 * 1000;
      localStorage.setItem(ZIP_CODE_CONSTANTS.EXPIRATION_KEY, expirationTime.toString());
    }
  }

  /**
   * Function to remove key values from localstorage , set data and show ZIP Modal
   */
  public showZipModal(): void {
    localStorage.removeItem(ZIP_CODE_CONSTANTS.ZIP_CODE_KEY);
    localStorage.removeItem(ZIP_CODE_CONSTANTS.EXPIRATION_KEY);
    const defaultBrowseZipCode = this.getDefaultZipCode().postalCode;
    if (defaultBrowseZipCode) {
      this.setZipCode(null, true, true);
    }
    this.stateStoreUtilsService.setBillingAddress(null);
    this.zipCodeNotAvailableSource.next(true);
  }

  /**
   * function to return zipcode value from localstorage
   * @returns zipcode
   */
  public getStoredZipCode(): string {
    let zipCode: string = null;
    try {
      const storedZipCode = localStorage.getItem(ZIP_CODE_CONSTANTS.ZIP_CODE_KEY);
      if (storedZipCode && !this.isZipCodeExpired()) {
        zipCode = storedZipCode;
      }
    } catch (error) {
      console.error('Stored ZIP Code read error', error);
    }
    return zipCode;
  }

  /**
   * Function to check expiry date of- a zipcode
   * @returns boolean value
   */
  private isZipCodeExpired(): boolean {
    const expirationTime = localStorage.getItem(ZIP_CODE_CONSTANTS.EXPIRATION_KEY);
    if (!expirationTime) {
      return true; // Expired if expiration time not set
    }
    return Date.now() > Number(expirationTime);
  }

  public isAnon(): boolean {
    const accountId = this.stateStoreUtilsService.getAddFan()
      ? ''
      : this.stateStoreUtilsService.getAccountId();
    return !accountId || accountId === SHARED_BROWSE_CONSTANTS.ANON;
  }

  public createCTA(ctaName?: string, ctaClass?: string, ctaType?: string): TmoButton {
    let Cta: TmoButton = {
      id: ctaName,
      name: ctaName,
      class: ctaClass,
      type: ctaType,
    };
    return Cta;
  }
}
